import React, { ChangeEvent } from 'react'
import { useTable } from 'react-table'
import Pagination from 'react-bootstrap/Pagination'
import { SortingType, UserColumns, UserTypes } from '../../shared/models/adminModels';
import TextInput from '../TextInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames'

interface IProps {
    columns: any[];
    data: any[];
    totalPages: number;
    fetchData: Function;
    isLoading: boolean;
    currentPage: number;
    pageSize: number;
    orderBy: string;
    sortType: string;
    searchParameter: string;
    userType: UserTypes;
    updateSearchParams: Function;
    headerclass?: string;
    rowClass?: string;
    onRowClick? : Function;
    firstHeadClass?: string;
}

enum PaginationAction {
    'FIRST'= 'First',
    'PREVIOUS'= 'Previous',
    'NEXT'= 'Next',
    'LAST'= 'Last'
}

// Define a default UI for filtering
function GlobalFilter(params: any) {
    const { 
        currentPage, orderBy, pageSize, 
        sortType, userType, searchParameter, updateSearchParams, fetchData } = params;
    const doSearch = (e: KeyboardEvent) => {
        e.which === 13 && 
        fetchData({
            currentPage,
            pageSize,
            orderBy,
            searchParameter,
            sortType
        })
       
    }
    return (
        <div className="admin-list-search-row">
           <div className='admin-list-search-textbox'>
                <TextInput
                    // onChange={(e: ChangeEvent<HTMLInputElement>) => debounceCall(e, params)}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => updateSearchParams({
                        currentPage,
                        orderBy,
                        sortType,
                        pageSize,
                        searchParameter: e.target.value
                    }, userType)}
                    onKeyPress ={
                        doSearch
                    }
                    placeholder={`Search by Name`}
                    value={searchParameter} />
                    {searchParameter && <span 
                    
                    onClick={() => fetchData({
                    currentPage,
                    pageSize,
                    orderBy,
                    searchParameter: '',
                    sortType
                })}><FontAwesomeIcon icon={faTimes} /></span>}
            </div>
            <div>
                <button onClick={() => fetchData({
                        currentPage,
                        pageSize,
                        orderBy,
                        searchParameter,
                        sortType
                    })} 
                    type="button" 
                    className="btn btn-primary search-btn"
                    disabled={!searchParameter}
                >
                    Search
                </button>
            </div>
         
        </div>
    )
}

const handlePagination = (params: any) => {
    const { searchParameter, orderBy, pageSize, currentPage, 
        totalPages, fetchData, sortType } = params;
    let goToPage: number; 

    switch(params.action) {
        case PaginationAction.FIRST: {
            goToPage = 1;
            break;
        }
        case PaginationAction.PREVIOUS: {
           goToPage = currentPage - 1;
           break;
        }
        case PaginationAction.NEXT: {
            goToPage = currentPage + 1;
            break;
        } case PaginationAction.LAST: {
            goToPage = totalPages;
            break;
        } default: {
            goToPage = currentPage;
            break;
        }
    }

    return fetchData({
        currentPage: goToPage,
        pageSize,
        orderBy,
        searchParameter,
        sortType
    });
}

const handleSorting = (params: any) => {
    const { fetchData, column, pageSize, 
        searchParameter, sortType, orderBy } = params;
    let selectedColumn = column.id;
    let newSortType: SortingType = SortingType.NULL;

    Object.values(UserColumns).forEach((value) => {
        if (value === selectedColumn && orderBy === value) {
            if (sortType === SortingType.ASC) {
                newSortType = SortingType.DESC
            } else if (sortType === SortingType.DESC) {
                newSortType = SortingType.NULL;
                selectedColumn = '';
            } else {
                newSortType = SortingType.ASC;
            }
        } else if (value === selectedColumn && orderBy !== value) {
            newSortType = SortingType.ASC;
        }
    });
    return fetchData({
        currentPage: 1,
        pageSize,
        orderBy: selectedColumn,
        searchParameter,
        sortType: newSortType
    });
}

const Table = (props: IProps) => {
    const { 
        columns, 
        data,
        totalPages,
        fetchData,
        isLoading,
        currentPage,
        pageSize,
        orderBy,
        sortType,
        searchParameter,
        userType,
        updateSearchParams,
        headerclass,
        rowClass,
        firstHeadClass
    } = props;

    // Use the state and functions returned from useTable to build your UI
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        visibleColumns
    } = useTable(
        {
            columns,
            data,
            initialState: { pageIndex: 0 },
            manualFilters: true,
            manualPagination: true,
            totalPages
        },
    );

    // Render the UI for your table
    return (
         <>
            <GlobalFilter
                currentPage={1}
                fetchData={fetchData}
                orderBy={orderBy}
                pageSize={pageSize}
                sortType={sortType}
                userType={userType}
                updateSearchParams={updateSearchParams}
                searchParameter={searchParameter}
            />
            {data.length ? <table className="table" {...getTableProps()}>
                <thead>
                    {headerGroups.map(headerGroup => (
                        <tr {...headerGroup.getHeaderGroupProps()} className={classNames(headerclass)}>
                            {headerGroup.headers.map((column, index) => (
                                <th
                                    {...column.getHeaderProps()}
                                    className = { classNames(index === 0 ? `table-head ${firstHeadClass}` : 'table-head')}
                                    onClick = {
                                        () => handleSorting({
                                            pageSize, searchParameter, fetchData, column, orderBy,
                                            sortType
                                        })
                                    } 
                                >
                                    {column.render('Header')}
                                    <span className="sorting-arrow">
                                        {orderBy === column.id && sortType
                                        &&
                                        <FontAwesomeIcon icon={(sortType === SortingType.DESC) ? faSortDown : faSortUp} />
                                        }
                                    </span>
                                    {/* Add a sort direction indicator */}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {rows.map((row, i) => {
                        prepareRow(row)
                        return (
                            <tr {...row.getRowProps()} className={classNames(rowClass,{'cursor-pointer': props.onRowClick})} onClick={() =>  props.onRowClick ? props.onRowClick(row) : null }>
                                {row.cells.map(cell => {
                                    return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                                })}
                            </tr>
                        )
                    })}
                </tbody>
            </table>  : null }
            
            { data.length ? <div className='pagination-container'>
                <Pagination className="">
                    <Pagination.First onClick={
                        () => handlePagination({
                            totalPages, currentPage, pageSize, orderBy, searchParameter, sortType,
                            fetchData, action: PaginationAction.FIRST
                        })
                    } disabled={currentPage === 1} />
                    <Pagination.Prev onClick={
                        () => handlePagination({
                            totalPages, currentPage, pageSize, orderBy, searchParameter, sortType,
                            fetchData, action: PaginationAction.PREVIOUS
                        })
                    } disabled={currentPage === 1} />
                    <Pagination.Item>{currentPage} of {totalPages}</Pagination.Item>
                    <Pagination.Next onClick={
                        () => handlePagination({
                            totalPages, currentPage, pageSize, orderBy, searchParameter, sortType,
                            fetchData, action: PaginationAction.NEXT
                        })
                    } disabled={currentPage === totalPages} />
                    <Pagination.Last onClick={
                        () => handlePagination({
                            totalPages, currentPage, pageSize, orderBy, searchParameter, sortType,
                            fetchData, action: PaginationAction.LAST
                        })
                    } disabled={currentPage === totalPages} />
                </Pagination>
                <div className='pagination_size-container'>
                    <label className='pagination_size-label'>Show</label>
                    <select onChange={(e) => fetchData({
                         currentPage: 1,
                         pageSize: e.target.value,
                         orderBy,
                         searchParameter,
                         sortType
                    })} value={pageSize}>
                        {[10, 20, 30, 40, 50].map(pageSize => (
                            <option key={pageSize} value={pageSize}>
                                {pageSize}
                            </option>
                        ))}
                    </select>
                </div>
            </div> : null }
            {!data.length ? <h5 className=''>No Records Found</h5> : null}
        </>
    )
}

export default Table
